/**
 * src/engine/jobs/vendorRegistrySync.js
 *
 * Engine action handler for 'vendor_sync'.
 *
 * For each active vendor with a registryId + registryCountry:
 *   1. Fetches fresh data from the appropriate Nordic registry API.
 *   2. Computes a new registryRiskScore (1–5).
 *   3. Updates the vendor's registryData, registryRiskScore, lastRegistrySync.
 *   4. If the new registryRiskScore > previous score (risk worsened),
 *      creates a simple-review task assigned to the vendor owner.
 *
 * Returns { changed, summary, mutations }
 * where mutations is an array of (state) => void functions.
 *
 * The vendor half of step 3 is NOT one of those mutations (GH #6185).
 * `vendors` is authoritative in tenant_vendor_state, and state-write strips
 * VENDOR_STATE_KEYS out of the blob payload for every tenant whose vendor
 * store row exists, while still answering 200. A vendor update returned as a
 * state mutation therefore reached the server and was dropped, silently: the
 * review task in step 4 persisted (adhocTasks still lives in the blob) while
 * the record it points at kept its old score and never got a lastRegistrySync
 * stamp. The task said the vendor had gone bankrupt and the vendor record
 * disagreed. Every vendor field this job touches now travels through
 * vendors-write, and only the task stays a state mutation.
 */

import { realEmployees } from '../../utils/sampleRecords.js';
import { generateId, now } from '../../utils/formatting.js';
import { fetchVendorByRegistryId, computeRegistryRiskScore } from '../../api/vendorRegistryApi.js';
import { updateVendor } from '../../api/vendorApi.js';

// This job runs in the background, with no dialog of its own to render an
// error into. The app-shell WriteErrorBanner outlives every route and is the
// only surface a failed background write has — the same event the control,
// document and risk write paths raise for the same reason.
function dispatchWriteError() {
  try {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent('seasuite:write-error'));
  } catch {
    /* non-browser environment (tests, SSR) — no-op */
  }
}

/**
 * Returns true if an open engine-generated review task for this exact
 * prevScore→newScore transition already exists (avoids duplicate tasks from
 * multiple syncs, but allows a new task when the score worsens again).
 */
function reviewTaskExists(state, vendorId, prevScore, newScore) {
  return (state.adhocTasks || []).some(
    (t) =>
      t.engineGenerated &&
      t.linkedEntityId === vendorId &&
      t.linkedEntityType === 'vendor' &&
      t.title.includes(`(${prevScore}→${newScore})`) &&
      t.status !== 'closed'
  );
}

export async function runVendorRegistrySync(actionConfig, state, { tenantId } = {}) {
  const vendors = (state.vendors || []).filter(
    (v) => v.registryId && v.registryCountry && v.status === 'active'
  );

  if (vendors.length === 0) {
    return { changed: false, summary: 'No vendors with registry data found.', mutations: [] };
  }

  // Without a tenant there is no vendors-write to issue, and the blob is not a
  // fallback — it is where these keys go to be stripped (see the header note).
  // Refuse the run and say so in the summary, which lands in the engine run
  // log, rather than doing work whose result cannot be stored.
  if (!tenantId) {
    return {
      changed: false,
      summary: 'Registry sync skipped: no tenant context, so vendor updates could not be written.',
      mutations: [],
    };
  }

  const mutations = [];
  const summaryParts = [];
  let anyChanged = false;

  for (const vendor of vendors) {
    try {
      const freshData = await fetchVendorByRegistryId(vendor.registryId, vendor.registryCountry);
      if (!freshData) {
        summaryParts.push(`${vendor.name}: no data returned`);
        continue;
      }

      const newScore = computeRegistryRiskScore(freshData);
      const prevScore = vendor.registryRiskScore || 1;
      const scoreWorsened = newScore > prevScore;

      const updatedVendor = {
        ...vendor,
        registryData: freshData,
        registryRiskScore: newScore,
        lastRegistrySync: now(),
        // Persist previous score so UI can show the delta
        previousRegistryRiskScore: prevScore,
      };

      // Write 1 — the vendor record, through its own store (GH #6185).
      //
      // Awaited before anything else in this iteration: the review task below
      // exists to tell someone the score moved, and raising it for a change
      // the register never took is the disagreement this fix removes. A
      // refusal ends this vendor's iteration — the next scheduled run recomputes
      // the same transition from the same unchanged record and retries, and
      // reviewTaskExists() still guards the task against duplication when it
      // does (ENG-B-004).
      let stored;
      try {
        const res = await updateVendor(updatedVendor, tenantId);
        // Prefer the server's copy: vendors-write stamps updatedAt, and holding
        // a stale one here makes the NEXT edit to this vendor fail its
        // optimistic-concurrency check as a conflict the user did not cause.
        stored = res?.vendor || updatedVendor;
      } catch (writeErr) {
        // A 409 here is vendors-write's record-level optimistic-concurrency
        // check (handleUpdate, keyed on updatedAt) refusing a payload built
        // from a state snapshot someone has since edited. That is the guard
        // working: nothing was overwritten, and the next scheduled run rebuilds
        // the update from the fresh record. It is the expected outcome of
        // concurrent editing rather than a failure the user must act on, so it
        // is recorded in the run log without also raising the app-shell banner
        // — the same line azureClient draws for a stale-version conflict.
        const isConflict = writeErr?.httpStatus === 409;
        if (!isConflict) dispatchWriteError();
        summaryParts.push(
          isConflict
            ? `${vendor.name}: registry data fetched but not saved — the record changed while syncing; will retry next run`
            : `${vendor.name}: registry data fetched but NOT saved (${writeErr.message})`
        );
        continue;
      }

      // Mutation 1 — echo the stored record into local state so the register
      // reflects the new score without waiting for a reload. The blob save this
      // arms carries `vendors` too and has it stripped server-side, which is
      // harmless: tenant_vendor_state is authoritative and state-read overlays
      // it on the next load.
      mutations.push((s) => {
        s.vendors = (s.vendors || []).map((v) => (v.id === vendor.id ? stored : v));
      });
      anyChanged = true;

      let taskNote = '';
      if (scoreWorsened && !reviewTaskExists(state, vendor.id, prevScore, newScore)) {
        const SCORE_LABELS = { 1: 'Low', 2: 'Elevated', 3: 'Medium', 4: 'High', 5: 'Critical' };
        const signals = [];
        if (freshData.bankrupt) signals.push('company is bankrupt');
        if (freshData.dissolved) signals.push('company has dissolved');
        if (freshData.inLiquidation) signals.push('company is in liquidation');
        if (freshData.forcedWinding) signals.push('company is under forced winding');

        const signalText =
          signals.length > 0
            ? `Registry signals: ${signals.join(', ')}.`
            : `Registry risk score changed from ${SCORE_LABELS[prevScore]} (${prevScore}) to ${SCORE_LABELS[newScore]} (${newScore}).`;

        const dueDateMs = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days

        // vendor.ownerId references an employee record — a task's assigneeId
        // is a tenant user id, so resolve through the employee's linked user
        // account (an employee without one, or without an owner, gets an
        // unassigned task rather than a task silently assigned to nobody's inbox).
        // A sample owner gets an unassigned task, never a sample assignee (GH #7870).
        const ownerEmployee = realEmployees(state.employees || []).find(
          (e) => e.id === vendor.ownerId
        );

        const task = {
          id: 'task_' + generateId(),
          title: `Review vendor risk score: ${vendor.name} (${prevScore}→${newScore})`,
          description: `The monthly registry sync for **${vendor.name}** has detected changes that affect the vendor risk score.\n\n${signalText}\n\nPlease review the updated registry data and confirm whether the new risk level is accepted or if mitigation actions are required.`,
          type: 'automatic',
          taskType: 'simple-review',
          status: 'open',
          dueDate: new Date(dueDateMs).toISOString(),
          assigneeId: ownerEmployee?.linkedUserId || null,
          controls: vendor.controls || [],
          linkedEntityId: vendor.id,
          linkedEntityType: 'vendor',
          engineGenerated: true,
          createdAt: now(),
          evidence: [],
        };

        // Mutation 2 — create review task (as adhoc task)
        mutations.push((s) => {
          s.adhocTasks = [task, ...(s.adhocTasks || [])];
        });
        taskNote = ` → review task created (score: ${prevScore}→${newScore})`;
      } else if (scoreWorsened) {
        taskNote = ` → open review task already exists`;
      }

      summaryParts.push(`${vendor.name}: score ${prevScore}→${newScore}${taskNote}`);
    } catch (err) {
      summaryParts.push(`${vendor.name}: fetch failed (${err.message})`);
    }
  }

  return {
    changed: anyChanged,
    summary: summaryParts.join('; ') || 'No vendors updated',
    mutations,
  };
}
